import * as React from "react";
import { useMutation, useQuery } from "convex/react";
import { AlertTriangle, Pencil, Trash2 } from "lucide-react";
import { toast } from "sonner";

import { api } from "$convex/convexApi";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from "@/components/ui/alert-dialog";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { DatePicker } from "@/components/ui/date-picker";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { Label } from "@/components/ui/label";
import { Switch } from "@/components/ui/switch";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";
import { Textarea } from "@/components/ui/textarea";
import { useAuthIdentity } from "@/contexts/UserProfileContext";
import { cn } from "@/lib/utils";
import { useAppLocale } from "@/lib/i18n";
import { m } from "@/paraglide/messages.js";

type CarrierNoticeStatus = "none" | "active" | "inactive" | "expired";

type CarrierNoticeRow = {
  carrierId: string;
  carrierName: string;
  shippeekCode?: string | null;
  status: CarrierNoticeStatus;
  notice: {
    _id: string;
    message: string;
    active: boolean;
    expiresAt?: number | null;
    updatedAt: number;
    updatedByName?: string | null;
    updatedByEmail?: string | null;
  } | null;
};

const MAX_MESSAGE_LENGTH = 500;

function getTodayDateString() {
  const today = new Date();
  return formatDateInputValue(today);
}

function formatDateInputValue(date: Date) {
  const year = date.getFullYear();
  const month = String(date.getMonth() + 1).padStart(2, "0");
  const day = String(date.getDate()).padStart(2, "0");
  return `${year}-${month}-${day}`;
}

function timestampToDateInputValue(timestamp?: number | null) {
  if (!timestamp) return "";
  return formatDateInputValue(new Date(timestamp));
}

function dateInputValueToEndOfDayTimestamp(value: string) {
  if (!value) return undefined;
  return new Date(`${value}T23:59:59.999`).getTime();
}

function formatDateTime(timestamp?: number | null) {
  if (!timestamp) return "—";
  return new Date(timestamp).toLocaleString(undefined, {
    month: "short",
    day: "numeric",
    year: "numeric",
    hour: "numeric",
    minute: "2-digit",
  });
}

function formatDate(timestamp?: number | null) {
  if (!timestamp) return m.carrier_notices_no_expiration();
  return new Date(timestamp).toLocaleDateString(undefined, {
    month: "short",
    day: "numeric",
    year: "numeric",
  });
}

function getDefaultMessage(carrierName: string) {
  return m.carrier_notices_default_message({ carrier: carrierName });
}

function getStatusLabel(status: CarrierNoticeStatus) {
  switch (status) {
    case "active":
      return m.status_active();
    case "inactive":
      return m.carrier_notices_status_inactive();
    case "expired":
      return m.status_expired();
    case "none":
      return m.carrier_notices_status_none();
  }
}

function getStatusClassName(status: CarrierNoticeStatus) {
  switch (status) {
    case "active":
      return "border-green-200 bg-green-50 text-green-700 dark:border-green-500/30 dark:bg-green-500/10 dark:text-green-300";
    case "expired":
      return "border-amber-200 bg-amber-50 text-amber-700 dark:border-amber-500/30 dark:bg-amber-500/10 dark:text-amber-300";
    case "inactive":
      return "border-slate-200 bg-slate-50 text-slate-600 dark:border-slate-500/30 dark:bg-slate-500/10 dark:text-slate-300";
    case "none":
      return "border-muted bg-muted/40 text-muted-foreground";
  }
}

function CheckoutNoticePreview({ message }: { message: string }) {
  const trimmedMessage = message.trim();

  return (
    <div className="rounded-lg border bg-card p-3">
      <p className="mb-2 text-xs font-medium text-muted-foreground">
        {m.carrier_notices_checkout_preview()}
      </p>
      {trimmedMessage ? (
        <div className="flex gap-2 rounded-md border border-amber-200 bg-amber-50 p-3 text-xs text-amber-900 dark:border-amber-500/30 dark:bg-amber-500/10 dark:text-amber-100">
          <AlertTriangle className="mt-0.5 h-4 w-4 shrink-0 text-amber-600 dark:text-amber-300" />
          <p className="whitespace-pre-line">{trimmedMessage}</p>
        </div>
      ) : (
        <div className="rounded-md border border-dashed p-3 text-sm text-muted-foreground">
          {m.carrier_notices_preview_empty()}
        </div>
      )}
    </div>
  );
}

export default function CarrierNoticesPage() {
  useAppLocale();
  const { authUserId } = useAuthIdentity();
  const queryResult = useQuery(
    api.logistics.listCarrierNotices,
    authUserId ? { authUserId } : "skip",
  ) as { carriers: CarrierNoticeRow[] } | undefined;
  const upsertNotice = useMutation(api.logistics.upsertCarrierNotice);
  const deleteNotice = useMutation(api.logistics.deleteCarrierNotice);

  const rows = React.useMemo(
    () => (Array.isArray(queryResult?.carriers) ? queryResult.carriers : []),
    [queryResult?.carriers],
  );
  const isLoading = queryResult === undefined;

  const [editingRow, setEditingRow] = React.useState<CarrierNoticeRow | null>(null);
  const [message, setMessage] = React.useState("");
  const [active, setActive] = React.useState(true);
  const [expiresDate, setExpiresDate] = React.useState("");
  const [isSaving, setIsSaving] = React.useState(false);
  const [isDeleteDialogOpen, setIsDeleteDialogOpen] = React.useState(false);
  const [isDeleting, setIsDeleting] = React.useState(false);

  const openEditor = React.useCallback((row: CarrierNoticeRow) => {
    setEditingRow(row);
    setMessage(row.notice?.message ?? getDefaultMessage(row.carrierName));
    setActive(row.notice?.active ?? true);
    setExpiresDate(timestampToDateInputValue(row.notice?.expiresAt));
  }, []);

  const closeEditor = React.useCallback(() => {
    if (isSaving || isDeleting) return;
    setEditingRow(null);
    setMessage("");
    setActive(true);
    setExpiresDate("");
    setIsDeleteDialogOpen(false);
  }, [isDeleting, isSaving]);

  const handleSave = React.useCallback(async () => {
    if (!editingRow || !authUserId) return;

    const trimmedMessage = message.trim();
    if (active && trimmedMessage.length === 0) {
      toast.error(m.carrier_notices_active_message_required());
      return;
    }
    if (trimmedMessage.length > MAX_MESSAGE_LENGTH) {
      toast.error(m.carrier_notices_message_too_long({ max: MAX_MESSAGE_LENGTH }));
      return;
    }

    setIsSaving(true);
    try {
      await upsertNotice({
        authUserId,
        carrierId: editingRow.carrierId as any,
        message,
        active,
        expiresAt: dateInputValueToEndOfDayTimestamp(expiresDate),
      });
      toast.success(m.carrier_notices_saved());
      closeEditor();
    } catch (error) {
      toast.error(error instanceof Error ? error.message : m.carrier_notices_save_failed());
    } finally {
      setIsSaving(false);
    }
  }, [active, authUserId, closeEditor, editingRow, expiresDate, message, upsertNotice]);

  const handleDelete = React.useCallback(async () => {
    if (!editingRow?.notice || !authUserId) return;

    setIsDeleting(true);
    try {
      await deleteNotice({
        authUserId,
        noticeId: editingRow.notice._id as any,
      });
      toast.success(m.carrier_notices_deleted());
      setIsDeleteDialogOpen(false);
      closeEditor();
    } catch (error) {
      toast.error(error instanceof Error ? error.message : m.carrier_notices_delete_failed());
    } finally {
      setIsDeleting(false);
    }
  }, [authUserId, closeEditor, deleteNotice, editingRow]);

  const remainingCharacters = MAX_MESSAGE_LENGTH - message.length;

  return (
    <div className="container mx-auto space-y-6 p-6">
      <div>
        <h1 className="text-3xl font-bold tracking-tight">{m.carrier_notices_title()}</h1>
        <p className="mt-2 max-w-3xl text-muted-foreground">
          {m.carrier_notices_description()}
        </p>
      </div>

      <Card>
        <CardHeader>
          <CardTitle>{m.carrier_notices_table_title()}</CardTitle>
          <CardDescription>{m.carrier_notices_table_description()}</CardDescription>
        </CardHeader>
        <CardContent>
          <Table>
            <TableHeader>
              <TableRow>
                <TableHead>{m.carrier_notices_carrier()}</TableHead>
                <TableHead>{m.carrier_notices_status()}</TableHead>
                <TableHead>{m.carrier_notices_message()}</TableHead>
                <TableHead>{m.carrier_notices_expires()}</TableHead>
                <TableHead>{m.carrier_notices_updated()}</TableHead>
                <TableHead className="text-right">{m.carrier_notices_actions()}</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {isLoading ? (
                <TableRow>
                  <TableCell colSpan={6} className="py-8 text-center text-muted-foreground">
                    {m.carrier_notices_loading()}
                  </TableCell>
                </TableRow>
              ) : rows.length === 0 ? (
                <TableRow>
                  <TableCell colSpan={6} className="py-8 text-center text-muted-foreground">
                    {m.carrier_notices_no_carriers()}
                  </TableCell>
                </TableRow>
              ) : (
                rows.map((row) => (
                  <TableRow key={row.carrierId}>
                    <TableCell className="font-medium">
                      <div>{row.carrierName}</div>
                      {row.shippeekCode ? (
                        <div className="text-xs text-muted-foreground">{row.shippeekCode}</div>
                      ) : null}
                    </TableCell>
                    <TableCell>
                      <Badge variant="outline" className={cn("font-medium", getStatusClassName(row.status))}>
                        {getStatusLabel(row.status)}
                      </Badge>
                    </TableCell>
                    <TableCell className="max-w-md whitespace-normal text-sm text-muted-foreground">
                      {row.notice?.message ? (
                        <span className="line-clamp-2 whitespace-pre-line">{row.notice.message}</span>
                      ) : (
                        <span>{m.carrier_notices_status_none()}</span>
                      )}
                    </TableCell>
                    <TableCell>{formatDate(row.notice?.expiresAt)}</TableCell>
                    <TableCell>
                      {row.notice ? (
                        <div className="space-y-1">
                          <div>{formatDateTime(row.notice.updatedAt)}</div>
                          {(row.notice.updatedByName || row.notice.updatedByEmail) ? (
                            <div className="text-xs text-muted-foreground">
                              {row.notice.updatedByName ?? row.notice.updatedByEmail}
                            </div>
                          ) : null}
                        </div>
                      ) : (
                        "—"
                      )}
                    </TableCell>
                    <TableCell className="text-right">
                      <Button type="button" variant="outline" size="sm" onClick={() => openEditor(row)}>
                        <Pencil className="mr-2 h-4 w-4" />
                        {m.edit()}
                      </Button>
                    </TableCell>
                  </TableRow>
                ))
              )}
            </TableBody>
          </Table>
        </CardContent>
      </Card>

      <Dialog open={Boolean(editingRow)} onOpenChange={(open) => { if (!open) closeEditor(); }}>
        <DialogContent className="max-w-2xl">
          <DialogHeader>
            <DialogTitle>
              {editingRow?.notice ? m.carrier_notices_edit_title() : m.carrier_notices_create_title()}
            </DialogTitle>
            <DialogDescription>
              {editingRow ? m.carrier_notices_dialog_description({ carrier: editingRow.carrierName }) : ""}
            </DialogDescription>
          </DialogHeader>

          <div className="space-y-5 py-2">
            <div className="flex items-center justify-between rounded-lg border bg-card p-3">
              <div className="space-y-0.5">
                <Label htmlFor="carrier-notice-active">{m.status_active()}</Label>
                <p className="text-sm text-muted-foreground">
                  {m.carrier_notices_active_help()}
                </p>
              </div>
              <Switch id="carrier-notice-active" checked={active} onCheckedChange={setActive} />
            </div>

            <div className="space-y-2">
              <Label htmlFor="carrier-notice-message">{m.carrier_notices_message()}</Label>
              <Textarea
                id="carrier-notice-message"
                value={message}
                onChange={(event) => setMessage(event.target.value)}
                placeholder={m.carrier_notices_message_placeholder()}
                rows={5}
                maxLength={MAX_MESSAGE_LENGTH}
              />
              <div className="flex items-center justify-between text-xs text-muted-foreground">
                <span>{m.carrier_notices_message_help()}</span>
                <span className={cn(remainingCharacters < 0 && "text-destructive")}>
                  {m.carrier_notices_characters_remaining({ count: remainingCharacters })}
                </span>
              </div>
            </div>

            <div className="space-y-2">
              <Label htmlFor="carrier-notice-expires">{m.carrier_notices_expires()}</Label>
              <div className="flex gap-2">
                <DatePicker
                  id="carrier-notice-expires"
                  value={expiresDate}
                  onChange={setExpiresDate}
                  minDate={getTodayDateString()}
                  placeholder={m.carrier_notices_no_expiration()}
                />
                {expiresDate ? (
                  <Button type="button" variant="outline" onClick={() => setExpiresDate("")}>
                    {m.carrier_notices_clear_expiration()}
                  </Button>
                ) : null}
              </div>
              <p className="text-xs text-muted-foreground">{m.carrier_notices_expiration_help()}</p>
            </div>

            <CheckoutNoticePreview message={message} />
          </div>

          <DialogFooter className="gap-2 sm:justify-between">
            <div>
              {editingRow?.notice ? (
                <Button
                  type="button"
                  variant="destructive"
                  onClick={() => setIsDeleteDialogOpen(true)}
                  disabled={isSaving || isDeleting}
                >
                  <Trash2 className="mr-2 h-4 w-4" />
                  {m.carrier_notices_delete()}
                </Button>
              ) : null}
            </div>
            <div className="flex gap-2">
              <Button type="button" variant="outline" onClick={closeEditor} disabled={isSaving || isDeleting}>
                {m.cancel()}
              </Button>
              <Button type="button" onClick={() => void handleSave()} disabled={isSaving || isDeleting}>
                {isSaving ? m.saving() : m.save_changes()}
              </Button>
            </div>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      <AlertDialog open={isDeleteDialogOpen} onOpenChange={setIsDeleteDialogOpen}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>{m.carrier_notices_delete_title()}</AlertDialogTitle>
            <AlertDialogDescription>
              {editingRow ? m.carrier_notices_delete_description({ carrier: editingRow.carrierName }) : ""}
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel disabled={isDeleting}>{m.cancel()}</AlertDialogCancel>
            <AlertDialogAction
              className="bg-destructive text-destructive-foreground hover:bg-destructive/90"
              disabled={isDeleting}
              onClick={(event) => {
                event.preventDefault();
                void handleDelete();
              }}
            >
              {isDeleting ? m.carrier_notices_deleting() : m.carrier_notices_delete()}
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
}
